import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  CircularProgress,
  CardActions
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { usePublicRegistrationFlow } from '../../../contexts/PublicRegistrationFlowContext';
import type { PricingPlan } from './PricingConfigs';
import { nodeApi } from '../../../config/apiClient';
import {
  StandardMarketingModal,
  marketingModalTextFieldSx,
} from './StandardMarketingModal';

const PricingCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[12],
  },
  '&.popular': {
    border: `3px solid ${theme.palette.primary.main}`,
    paddingTop: theme.spacing(4),
    '&::before': {
      content: '"En Popüler"',
      position: 'absolute',
      top: 8,
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      padding: '4px 16px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      zIndex: 1
    }
  }
}));

interface PricingSectionProps {
  backgroundColor?: string;
  titleColor?: string;
  source?: string;
  pricingPlans?: PricingPlan[];
  title?: string;
}

const PricingSection: React.FC<PricingSectionProps> = ({ 
  backgroundColor = 'white',
  titleColor = '#0D47A1',
  source = 'general',
  pricingPlans: customPricingPlans,
  title
}) => {
  const { t } = useTranslation();
  const { openRoleSelection } = usePublicRegistrationFlow();
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [successModalOpen, setSuccessModalOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({
    nameSurname: '',
    email: '',
    phone: '',
    selectedPlan: ''
  });

  // Default QR Menu pricing plans
  const defaultPricingPlans: PricingPlan[] = [
    {
      name: 'Başlangıç',
      price: '499',
      period: 'Aylık',
      description: 'Temel özellikler ile başlamak için ideal plan.',
      features: [
        'QR Kod Oluşturma',
        'Temel Menü Yönetimi',
        'Görsel Destekli Menü',
        'Mobil Uyumlu Tasarım',
        'Sınırsız Görüntülenme',
        'Anlık Menü Güncelleme',
        'Yazar Kasa / POS Entegrasyonu (siparişlerin kasaya otomatik düşmesi)',
        'Hızlı Destek'
      ],
      buttonText: t('qrMenu.pricing.buttonText'),
      action: 'register',
      popular: false
    },
    {
      name: 'Profesyonel',
      price: '1250',
      period: 'Aylık',
      description: 'Gelişmiş özellikler ile işletmenizi büyütün.',
      features: [
        'Profesyonel Menü Temaları',
        'Sipariş Yönlendirme / Müşteri Ekranı',
        'Yazar Kasa / POS Entegrasyonu (siparişlerin kasaya otomatik düşmesi)',
        'Mutfak Ekranı (KDS)',
        'Restoran Profil Sayfası',
        'Detaylı Analitik Raporlar',
        'KDV & Fiyatlama Ayarları',
        'Garson – mutfak – yönetici rolleri - yetkilendirme',
        'Ön Muhasebe Modülü (Günlük kasa giriş/çıkışları)',
        'Çoklu Dil Desteği',
        'Öncelikli Destek'
      ],
      buttonText: t('qrMenu.pricing.buttonText'),
      action: 'register',
      popular: true
    },
    {
      name: 'Kurumsal',
      price: 'ÖZEL',
      description: 'Kurumsal ihtiyaçlar için özel çözümler.',
      features: [
        'Yazar Kasa / POS Entegrasyonu (siparişlerin kasaya otomatik düşmesi)',
        'Gelişmiş Ön Muhasebe Modülü (finansal süreçlerin tek panelden yönetimi)',
        'Stok Takip Modülü (maliyet, depo ve şube stoklarının kontrolü)',
        'E-Ticaret Modülü (online sipariş ve ödeme sistemi)',
        'Şube Yönetim Sistemi (tüm şubeleri tek merkezden yönetme)',
        'Cihaz Yönetimi Modülü (tüm cihazları tek panelden kontrol etmenizi sağlar)',
        'Merkezi Yönetim Paneli (kurumsal yöneticiler için üst düzey kontrol)',
        'Görev Takip Modülü (işletme içi süreçleri düzenli şekilde yönetmenizi sağlar)'
      ],
      buttonText: t('qrMenu.pricing.contactButtonText'),
      action: 'contact',
      popular: false
    }
  ];

  // Use custom pricing plans if provided, otherwise use default
  const pricingPlans = customPricingPlans || defaultPricingPlans;
  const sectionTitle = title || t('ecommerce.pricing.title');

  const handleOpenContactModal = (planName: string) => {
    setFormData(prev => ({ ...prev, selectedPlan: planName }));
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await nodeApi.post('/api/contact', {
        ...formData,
        source: source
      });

      setContactModalOpen(false);
      setSuccessModalOpen(true);
      setFormData({
        nameSurname: '',
        email: '',
        phone: '',
        selectedPlan: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box sx={{ py: 8, backgroundColor }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            textAlign="center" 
            sx={{ 
              mb: 6, 
              fontWeight: 700, 
              color: titleColor, 
              fontSize: { xs: '2rem', md: '3rem' } 
            }}
          >
            {sectionTitle}
          </Typography>
          <Grid container spacing={4}>
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <PricingCard className={plan.popular ? 'popular' : ''}>
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                    <Typography 
                      variant="h4" 
                      gutterBottom 
                      sx={{ fontWeight: 600, color: '#333' }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ color: '#666', mb: 3 }}
                    >
                      {plan.description}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography 
                        variant="h3" 
                        component="span"
                        sx={{ fontWeight: 800, color: titleColor }}
                      >
                        {plan.price === 'ÖZEL' ? plan.price : `₺${plan.price}`}
                      </Typography>
                      {plan.period && (
                        <Typography 
                          variant="h6" 
                          component="span"
                          sx={{ color: '#666', ml: 1 }}
                        >
                          /{plan.period}
                        </Typography>
                      )}
                    </Box>
                    <List sx={{ mb: 3 }}>
                      {plan.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle sx={{ color: '#4CAF50', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                            primaryTypographyProps={{ fontSize: '0.9rem' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant={plan.popular ? 'contained' : 'outlined'}
                      fullWidth
                      size="large"
                      onClick={() => (plan.action === 'register' ? openRoleSelection() : handleOpenContactModal(plan.name))}
                      sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                    >
                      {plan.buttonText}
                    </Button>
                  </CardActions>
                </PricingCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <StandardMarketingModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        titleId="pricing-contact-modal-title"
        title={
          <>
            {formData.selectedPlan} {t('ecommerce.modal.contactTitle')}
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            {t('ecommerce.modal.contactDescription')}
          </Typography>
          <TextField
            name="nameSurname"
            label={t('ecommerce.modal.namePlaceholder')}
            value={formData.nameSurname}
            onChange={handleInputChange}
            fullWidth
            required
            margin="dense"
            sx={{ ...marketingModalTextFieldSx, mb: 1.5 }}
          />
          <TextField
            name="email"
            label={t('ecommerce.modal.emailPlaceholder')}
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            fullWidth
            required
            margin="dense"
            sx={{ ...marketingModalTextFieldSx, mb: 1.5 }}
          />
          <TextField
            name="phone"
            label={t('ecommerce.modal.phonePlaceholder')}
            value={formData.phone}
            onChange={handleInputChange}
            fullWidth
            required
            margin="dense"
            sx={{ ...marketingModalTextFieldSx, mb: 2 }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              type="button"
              onClick={handleCloseContactModal}
              disabled={isSubmitting}
              sx={{ color: 'text.secondary' }}
            >
              {t('ecommerce.modal.cancelButton')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : null
              }
              sx={{
                bgcolor: '#0D47A1',
                borderRadius: '12px',
                fontWeight: 'bold',
                px: 3,
                '&:hover': { bgcolor: '#0A3C87' },
              }}
            >
              {isSubmitting
                ? t('ecommerce.modal.sendingButton')
                : t('ecommerce.modal.sendButton')}
            </Button>
          </Box>
        </form>
      </StandardMarketingModal>

      <StandardMarketingModal
        open={successModalOpen}
        onClose={handleCloseSuccessModal}
        titleId="pricing-success-modal-title"
        title={t('ecommerce.modal.successTitle')}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('ecommerce.modal.successMessage')}
          </Typography>
          <Button
            onClick={handleCloseSuccessModal}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#0D47A1',
              borderRadius: '12px',
              fontWeight: 'bold',
              py: 1.2,
              '&:hover': { bgcolor: '#0A3C87' },
            }}
          >
            {t('ecommerce.modal.closeButton')}
          </Button>
        </Box>
      </StandardMarketingModal>
    </>
  );
};

export default PricingSection;
